/**
 * City-level cost multipliers used to weight startup budget and monthly
 * staff cost calculations. Baseline: Miami, FL = 1.00.
 *
 * Sources: JLL, CBRE & Turner + Townsend 2025 market data.
 *
 * Lookup is case- and punctuation-insensitive on the city name. State/country
 * suffixes (", NY", ", FL", etc.) are stripped before matching, so callers can
 * pass either "New York" or "New York, NY".
 */

export const CITY_MULTIPLIER_SOURCE = "JLL, CBRE & Turner + Townsend 2025 market data";

export const CITY_MULTIPLIERS: Record<string, number> = {
  // USA
  "New York, NY": 1.72,
  "San Francisco, CA": 1.65,
  "Boston, MA": 1.47,
  "Los Angeles, CA": 1.39,
  "Seattle, WA": 1.33,
  "Miami, FL": 1.00,
  "Chicago, IL": 1.10,
  "Las Vegas, NV": 0.98,
  "Nashville, TN": 0.93,
  "Austin, TX": 0.96,
  "Atlanta, GA": 0.90,
  "Denver, CO": 0.95,
  "Orlando, FL": 0.87,
  "Dallas, TX": 0.90,
  "Houston, TX": 0.87,
  "New Orleans, LA": 0.84,
  "Portland, OR": 0.97,
  "Phoenix, AZ": 0.85,
  "San Diego, CA": 1.19,
  // Canada
  "Toronto": 1.07,
  "Vancouver": 1.09,
  // Brazil
  "São Paulo": 0.39,
  "Rio de Janeiro": 0.35,
  // Mexico
  "Mexico City": 0.36,
  "Cancún": 0.37,
  "Guadalajara": 0.30,
  "Monterrey": 0.32,
  "Tulum": 0.37,
  // Argentina
  "Buenos Aires": 0.28,
  // Colombia
  "Bogotá": 0.30,
  "Medellín": 0.27,
  "Cartagena": 0.28,
  // Peru
  "Lima": 0.29,
  // Uruguay
  "Montevideo": 0.31,
  // United Kingdom
  "London": 1.27,
  "Manchester": 0.97,
  "Edinburgh": 0.99,
  "Birmingham": 0.93,
  "Bristol": 0.97,
  "Leeds": 0.90,
  "Liverpool": 0.88,
  // France
  "Paris": 1.11,
  "Lyon": 0.90,
  "Nice": 0.95,
  "Bordeaux": 0.89,
  // Italy
  "Milan": 0.97,
  "Rome": 0.89,
  "Florence": 0.84,
  "Naples": 0.73,
  "Venice": 0.90,
  "Bologna": 0.79,
  // Spain
  "Barcelona": 0.87,
  "Madrid": 0.86,
  "Valencia": 0.74,
  "Marbella": 0.83,
  "Ibiza": 0.88,
  // Netherlands
  "Amsterdam": 1.08,
  // Germany
  "Munich": 1.03,
  "Berlin": 0.95,
  "Hamburg": 0.94,
  // Portugal
  "Lisbon": 0.71,
  // Greece
  "Athens": 0.62,
  "Mykonos": 0.77,
  "Santorini": 0.75,
  "Thessaloniki": 0.56,
  // Austria
  "Vienna": 0.97,
  // Switzerland
  "Zurich": 1.50,
  "Geneva": 1.54,
  "Crans-Montana": 1.44,
  // Czech Republic
  "Prague": 0.59,
  // Croatia
  "Dubrovnik": 0.63,
  "Split": 0.54,
  // Denmark
  "Copenhagen": 1.27,
  // Sweden
  "Stockholm": 1.18,
  // Poland
  "Kraków": 0.48,
  // UAE
  "Dubai": 1.04,
  "Abu Dhabi": 0.93,
  // Qatar
  "Doha": 0.92,
  // Saudi Arabia
  "Riyadh": 0.82,
  // Lebanon
  "Beirut": 0.32,
  // Japan
  "Tokyo": 1.17,
  "Osaka": 0.98,
  // Singapore
  "Singapore": 1.21,
  // Hong Kong
  "Hong Kong": 1.33,
  // Thailand
  "Bangkok": 0.39,
  "Phuket": 0.43,
  "Chiang Mai": 0.33,
  // Indonesia
  "Bali": 0.36,
  // Malaysia
  "Kuala Lumpur": 0.42,
  // South Korea
  "Seoul": 0.87,
  // China
  "Shanghai": 0.63,
  "Beijing": 0.60,
  // Australia
  "Sydney": 1.21,
  "Melbourne": 1.14,
  "Brisbane": 1.07,
  "Gold Coast": 1.02,
};

// Country-level fallback averages when an exact city isn't found.
const COUNTRY_FALLBACK: Record<string, number> = {
  "united states": 1.00, usa: 1.00, us: 1.00,
  canada: 1.08,
  brazil: 0.37,
  mexico: 0.34,
  argentina: 0.28,
  colombia: 0.28,
  peru: 0.29,
  uruguay: 0.31,
  "united kingdom": 1.00, uk: 1.00,
  france: 0.96,
  italy: 0.85,
  spain: 0.84,
  netherlands: 1.08,
  germany: 0.97,
  portugal: 0.71,
  greece: 0.68,
  austria: 0.97,
  switzerland: 1.49,
  "czech republic": 0.59,
  croatia: 0.58,
  denmark: 1.27,
  sweden: 1.18,
  poland: 0.48,
  "united arab emirates": 0.99, uae: 0.99,
  qatar: 0.92,
  "saudi arabia": 0.82,
  lebanon: 0.32,
  japan: 1.07,
  singapore: 1.21,
  "hong kong": 1.33,
  thailand: 0.38,
  indonesia: 0.36,
  malaysia: 0.42,
  "south korea": 0.87,
  china: 0.62,
  australia: 1.11,
};

function normalize(s: string): string {
  return s.toLowerCase().trim();
}

/** Strip a trailing ", XX" state/country suffix, returning just the city. */
function stripSuffix(city: string): string {
  const idx = city.indexOf(",");
  return idx === -1 ? city : city.slice(0, idx).trim();
}

const NORMALIZED_TABLE: Record<string, number> = Object.fromEntries(
  Object.entries(CITY_MULTIPLIERS).map(([k, v]) => [normalize(k), v]),
);
const NORMALIZED_CITY_ONLY: Record<string, number> = Object.fromEntries(
  Object.entries(CITY_MULTIPLIERS).map(([k, v]) => [normalize(stripSuffix(k)), v]),
);

/**
 * Look up the combined cost multiplier for a city. Falls back to country
 * average when the city isn't listed, and finally to 1.00 (Miami baseline).
 */
export function getCityMultiplier(city: string | undefined | null, country?: string): number {
  if (!city) return countryFallback(country);
  const raw = normalize(city);
  if (NORMALIZED_TABLE[raw] !== undefined) return NORMALIZED_TABLE[raw];
  const bare = normalize(stripSuffix(city));
  if (NORMALIZED_CITY_ONLY[bare] !== undefined) return NORMALIZED_CITY_ONLY[bare];
  return countryFallback(country);
}

function countryFallback(country?: string): number {
  if (!country) return 1.0;
  return COUNTRY_FALLBACK[normalize(country)] ?? 1.0;
}